import axios from 'axios';
import {
  DescribedRow,
  NewPriceFieldRow,
  OrderedRow,
  PriceRows,
  PriceRowsResponse,
} from '../models/priceRows.model';

const areaUrl = (projectSlug: string, areaSlug: string) =>
  `/api/project/${projectSlug}/areas/${areaSlug}`;

const orderedRowOf = (row: DescribedRow): OrderedRow => {
  switch (row.kind) {
    case 'sum':
      return { kind: 'sum', id: row._id, name: '' };
    case 'price':
    case 'text':
      return { kind: row.kind, id: row._id, name: row.name };
    case 'value':
      return { kind: 'value', id: row._id, name: row.name, unit: row.unit };
    case 'formula':
      return { kind: 'formula', id: row._id, name: row.name, unit: row.unit, formula: row.formula };
  }
};

const priceRowsOf = ({ areaId, priceFieldOrder, values }: PriceRowsResponse): PriceRows => ({
  areaId,
  rows: priceFieldOrder.map(orderedRowOf),
  values,
});

export const getPriceRows = async (projectSlug: string, areaSlug: string): Promise<PriceRows> => {
  const { data } = await axios.get<PriceRowsResponse>(
    `${areaUrl(projectSlug, areaSlug)}/priceRows`,
  );
  return priceRowsOf(data);
};

export const getProjectPriceRows = async (
  projectSlug: string,
  signal?: AbortSignal,
): Promise<PriceRows[]> => {
  const { data } = await axios.get<PriceRowsResponse[]>(`/api/project/${projectSlug}/priceRows`, {
    signal,
  });
  return data.map(priceRowsOf);
};

export const updatePriceFieldOrder = async (
  projectSlug: string,
  areaSlug: string,
  priceFieldOrder: string[],
): Promise<void> => {
  await axios.put(`${areaUrl(projectSlug, areaSlug)}/priceFieldOrder`, { priceFieldOrder });
};

export const addCustomRow = async (
  projectSlug: string,
  areaSlug: string,
  row: NewPriceFieldRow,
): Promise<OrderedRow[]> => {
  const { data } = await axios.post<DescribedRow[]>(
    `${areaUrl(projectSlug, areaSlug)}/customRows`,
    row,
  );
  return data.map(orderedRowOf);
};

export const updateCustomRow = async (
  projectSlug: string,
  areaSlug: string,
  customRowId: string,
  row: NewPriceFieldRow,
): Promise<OrderedRow[]> => {
  const { data } = await axios.put<DescribedRow[]>(
    `${areaUrl(projectSlug, areaSlug)}/customRows/${customRowId}`,
    row,
  );
  return data.map(orderedRowOf);
};

export const deleteCustomRow = async (
  projectSlug: string,
  areaSlug: string,
  customRowId: string,
): Promise<OrderedRow[]> => {
  const { data } = await axios.delete<DescribedRow[]>(
    `${areaUrl(projectSlug, areaSlug)}/customRows/${customRowId}`,
  );
  return data.map(orderedRowOf);
};

export const updateCustomRowValue = async (
  projectSlug: string,
  areaSlug: string,
  customRowId: string,
  {
    supplierId,
    refVersion,
    value,
  }: { supplierId: string; refVersion: number; value: number | string | null },
): Promise<void> => {
  await axios.put(`${areaUrl(projectSlug, areaSlug)}/customRows/${customRowId}/values`, {
    supplierId,
    refVersion,
    value,
  });
};
